import React, { useState, useContext, useEffect } from "react";
import styles from "./OrderPanel.module.css";
import { MarketContext } from "../../../context/MarketContext";
import { FaAngleDown } from "react-icons/fa6";
import { GoClock } from "react-icons/go";
import { toast } from "react-toastify";
import axios from "axios";
import { env } from "../../../constants/env";

const OrderPanel = ({ mobile }) => {
  const { selected, placeOrder, positions, tickers, balance } =
    useContext(MarketContext);

  // Local state
  const [time, setTime] = useState("30 sec");
  const [amount, setAmount] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [payoutPercent, setPayoutPercent] = useState(40);
  const [showTimeOptions, setShowTimeOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderCooldown, setOrderCooldown] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("userToken");

  // Payout rates by time option
  const payoutRates = {
    "30 sec": 1.4, // 140%
    "1 min": 1.6, // 160%
    "5 min": 1.8, // 180%
  };

  // Compute payout dynamically
  const payout = amount && payoutRates[time] ? amount * payoutRates[time] : 0;

  // -------------------------
  // Convert time string to seconds
  const getSeconds = (val) => {
    if (!val) return 30;
    const cleaned = val.trim().toLowerCase();
    const num = parseInt(cleaned.match(/\d+/)?.[0] || "30", 10);
    const unit = cleaned.match(/[a-z]+/)?.[0] || "sec";
    if (unit.startsWith("sec")) return num;
    if (unit.startsWith("min")) return num * 60;
    return num;
  };
  // -------------------------

  // Update remaining countdown for selected asset
  useEffect(() => {
    if (!selected || !positions) return;
    const pos = positions.find(
      (p) => p.symbol === selected.symbol && p.remaining > 0
    );
    setRemaining(pos ? pos.remaining : null);
  }, [positions, selected]);

  // Handle Buy/Sell
  const handleOrder = async (type) => {
    if (!selected || !selected.symbol) return;

    // ⛔ Cooldown active
    if (orderCooldown) {
      toast.error("Please wait 1 second before placing another order.");
      return;
    }

    try {
      setLoading(true);

      if (!amount || amount <= 0) {
        toast.error("Please enter a valid amount.");
        return;
      }

      if (amount > balance) {
        toast.error("Insufficient balance to place this order.");
        return;
      }

      // 🔒 Activate cooldown
      setOrderCooldown(true);
      setTimeout(() => setOrderCooldown(false), 1000); // ← 1 second lock

      const tradeId = Date.now(); // Unique ID

      // Send order request to backend
      const res = await axios.post(
        `${env.apiUrl}/api/user/place-order`,
        {
          symbol: selected.symbol,
          amount,
          tradeId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const ticker = tickers.find((t) => t.symbol === selected.symbol);
      const entryPrice = ticker?.price ?? selected.price;

      placeOrder(
        tradeId,
        type,
        amount,
        getSeconds(time),
        entryPrice,
        payoutPercent
      );
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;

    if (val === "") {
      setAmount(0);
      setError("");
      return;
    }

    const num = +val;

    if (num > 50) {
      setError("Maximum is 50 $PROP per trade");
    } else {
      setError("");
    }

    setAmount(num);
  };

  return (
    <div
      className={`${styles.panel} ${
        mobile ? styles.mobilePanel : ""
      } mt-3 d-flex flex-column align-items-center justify-content-center`}
    >
      <div className="d-flex align-items-end gap-3">
        {/* Time selection */}
        <div className={`${styles.timeBoxWrapper} ${mobile && "flex-fill"}`}>
          <label className={styles.label}>Time</label>
          <div
            className={`${styles.timeBox} ${
              mobile ? styles.timeBoxMobile : ""
            }`}
          >
            {showTimeOptions && (
              <div className={styles.timeValueOptions}>
                <div
                  className={`${styles.timeLabels} d-flex justify-content-between mb-1`}
                >
                  <span className="d-flex align-items-center gap-1">
                    <GoClock />
                    Timer
                  </span>
                  <span>Payout</span>
                </div>
                <div
                  onClick={() => {
                    setTime("30 sec");
                    setPayoutPercent(40);
                    setShowTimeOptions(false);
                  }}
                  className={`${styles.timeValueOption}  d-flex justify-content-between mb-2`}
                >
                  30 sec <span>40%</span>
                </div>
                <div
                  onClick={() => {
                    setTime("1 min");
                    setPayoutPercent(60);
                    setShowTimeOptions(false);
                  }}
                  className={`${styles.timeValueOption} d-flex justify-content-between`}
                >
                  1 min <span>60%</span>
                </div>
                <div
                  onClick={() => {
                    setTime("5 min");
                    setPayoutPercent(80);
                    setShowTimeOptions(false);
                  }}
                  className={`${styles.timeValueOption} d-flex justify-content-between mt-2`}
                >
                  5 min <span>80%</span>
                </div>
              </div>
            )}
            <div
              className="d-flex justify-content-between align-items-center w-100"
              onClick={() => setShowTimeOptions((prev) => !prev)}
              style={{ cursor: "pointer" }}
            >
              <span>{time}</span>
              <FaAngleDown color="#CECECE" style={{ flexShrink: 0 }} />
            </div>
          </div>
        </div>

        {/* Payout */}
        <div className={mobile && "flex-fill"}>
          <label className={styles.label}>Payout</label>
          <div className={styles.payoutBox}>
            {!mobile && <span>{payout.toFixed(0)} $PROP</span>}
            <small>{payoutPercent}%</small>
          </div>
        </div>

        {/* Amount input */}
        <div>
          <label className={styles.label}>Amount</label>
          <input
            type="number"
            min="1"
            step="1"
            value={amount === 0 ? "" : amount}
            onChange={handleChange}
            onWheel={(e) => e.target.blur()} // prevent scroll
            className={`${styles.input} ${error ? styles.error : ""}`}
          />
        </div>

        {/* Quick amount buttons */}
        {!mobile && (
          <>
            <div className="d-flex gap-2">
              {[25, 50].map((val) => (
                <div
                  key={val}
                  className={styles.numberBox}
                  onClick={() => setAmount(val)}
                >
                  {val}
                </div>
              ))}
            </div>

            {/* Buy/Sell buttons */}
            <div className="d-flex gap-2">
              <button
                className={`${styles.buyBtn} ${
                  orderCooldown ? "btn-disabled" : ""
                } flex-fill`}
                onClick={() => handleOrder("buy")}
                disabled={loading || orderCooldown}
              >
                Buy
              </button>
              <button
                className={`${styles.sellBtn} ${
                  orderCooldown ? "btn-disabled" : ""
                } flex-fill`}
                onClick={() => handleOrder("sell")}
                disabled={loading || orderCooldown}
              >
                Sell
              </button>
            </div>
          </>
        )}
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      {mobile && (
        <div className="d-flex w-100 gap-3 mt-2">
          <button
            className={`${styles.buyBtn} ${
              orderCooldown ? "btn-disabled" : ""
            } flex-fill text-center`}
            onClick={() => handleOrder("buy")}
            disabled={loading || orderCooldown}
          >
            Buy
          </button>
          <button
            className={`${styles.sellBtn} ${
              orderCooldown ? "btn-disabled" : ""
            }  flex-fill text-center`}
            onClick={() => handleOrder("sell")}
            disabled={loading || orderCooldown}
          >
            Sell
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderPanel;
